import { createTheme, darken, lighten } from '@mui/material';
import { ProductGroups } from 'enums';

type Backgrounds = {
  main: string;
  main_new: string;
  light: string;
  dark: string;
  success: string;
  warning: string;
  error: string;
  inactive: string;
  active: string;
  lightGreen: string;
  lightOrange: string;
  lightRed: string;
  lightViolet: string;
};

interface Greys {
  darkest: string;
  darker: string;
  dark: string;
  medium: string;
  light: string;
  lighter: string;
  lightest: string;
}

declare module '@mui/material/styles' {
  interface Palette {
    analysisSecondary: Palette['primary'];
    recruiting: Palette['primary'];
    lead: Palette['primary'];
    sales: Palette['primary'];
    backgrounds: Backgrounds;
    greys: Greys;
  }

  interface PaletteOptions {
    analysisSecondary: Palette['primary'];
    recruiting: Palette['primary'];
    lead: Palette['primary'];
    sales: Palette['primary'];
    backgrounds: Backgrounds;
    greys: Greys;
  }
}

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1537,
    },
  },
  typography: {
    fontFamily: ['Roboto', 'Arial', 'sans-serif'].join(','),
  },
  palette: {
    primary: {
      main: '#4F44FF',
      light: lighten('#4F44FF', 0.4),
      dark: darken('#4F44FF', 0.4),
      contrastText: '#FFF',
    },
    secondary: {
      main: '#007FFF',
      light: lighten('#007FFF', 0.25),
      dark: darken('#007FFF', 0.25),
      contrastText: '#FFF',
    },
    analysisSecondary: {
      main: '#FFA502',
      light: lighten('#FFA502', 0.25),
      dark: darken('#FFA502', 0.25),
      contrastText: '#FFF',
    },
    recruiting: {
      main: '#FFA502',
      light: lighten('#FFA502', 0.25),
      dark: darken('#FFA502', 0.25),
      contrastText: '#FFF',
    },
    lead: {
      main: '#FF0A71',
      light: lighten('#FF0A71', 0.25),
      dark: darken('#FF0A71', 0.25),
      contrastText: '#FFF',
    },
    sales: {
      main: '#7722FF',
      light: lighten('#7722FF', 0.25),
      dark: darken('#7722FF', 0.25),
      contrastText: '#FFF',
    },
    backgrounds: {
      main: '#EDECFF',
      main_new: '#F5F5FB',
      light: '#F6F6FB',
      dark: '#101031',
      success: '#daf696',
      warning: '#f5b971',
      error: '#fee',
      inactive: '#e8f0ff',
      active: '#daf696',
      lightGreen: '#daf696',
      lightOrange: '#fbe6c3',
      lightRed: '#f2caca',
      lightViolet: '#cfd6fb',
    },
    error: {
      main: '#F60404',
      light: lighten('#F60404', 0.4),
      dark: darken('#F60404', 0.4),
      contrastText: '#FFF',
    },
    warning: {
      main: '#ED6C02',
      light: lighten('#ED6C02', 0.4),
      dark: darken('#ED6C02', 0.4),
      contrastText: '#FFF',
    },
    success: {
      main: '#18C61F',
      light: lighten('#18C61F', 0.4),
      dark: darken('#18C61F', 0.4),
      contrastText: '#FFF',
    },
    greys: {
      darkest: '#373737',
      darker: '#444',
      dark: '#555',
      medium: '#707070',
      light: '#C9CCD3',
      lighter: '#E3E3E8',
      lightest: '#F6F6F6',
    },
    text: {
      primary: '#1C1C1C',
      secondary: '#707070',
      disabled: 'rgba(0,0,0,0.38)',
    },
  },
  components: {
    MuiButtonBase: {
      defaultProps: {
        focusRipple: true,
        disableRipple: true,
      },
    },
    // Handy (< md): iOS zoomt in Felder unter 16 px hinein – gilt für alle MUI-Eingaben.
    MuiInputBase: {
      styleOverrides: {
        input: {
          '@media (max-width: 899.95px)': {
            fontSize: 16,
          },
        },
      },
    },
    // Handy (< md): Dialoge mit 12 px statt 32 px Rand, damit Inhalte nicht abgeschnitten werden.
    MuiDialog: {
      styleOverrides: {
        paper: {
          '@media (max-width: 899.95px)': {
            margin: 12,
            maxWidth: 'calc(100% - 24px) !important',
          },
        },
        paperFullWidth: {
          '@media (max-width: 899.95px)': {
            width: 'calc(100% - 24px)',
          },
        },
        paperScrollPaper: {
          '@media (max-width: 899.95px)': {
            maxHeight: 'calc(100% - 24px)',
          },
        },
      },
    },
  },
});

export const ProductMappedColors = {
  default: theme.palette.primary.main,
  [ProductGroups.HR_BOOSTER]: theme.palette.recruiting.main,
  [ProductGroups.LEAD_BOOSTER]: theme.palette.lead.main,
};

export enum Shadows {
  GREY_SHADOW_MAIN = '5px 5px 40px 0 rgba(0, 0, 0, 0.16)',
  GREY_SHADOW_MEDIUM = '0 4px 12px 0 rgba(0, 0, 0, 0.30)',
  GREY_SHADOW_LIGHT = '0 4px 12px 0 rgba(0, 0, 0, 0.18)',
  GREY_SHADOW_LIGHTER = '0 4px 12px 0 rgba(0, 0, 0, 0.12)',
}

export default theme;
